import React, { useState } from "react";
import { X, Upload, Image as ImageIcon, Video, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

const CompleteJobDialog = ({ onClose, onSubmit }) => {
  const [pictures, setPictures] = useState([]);
  const [videos, setVideos] = useState([]);
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);

  const handlePictureUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPictures = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: Date.now() + Math.random(),
    }));
    setPictures((prev) => [...prev, ...newPictures]);
  };

  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newVideos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: Date.now() + Math.random(),
    }));
    setVideos((prev) => [...prev, ...newVideos]);
  };

  const removePicture = (id) => {
    setPictures((prev) => {
      const item = prev.find((p) => p.id === id);
      if (item) URL.revokeObjectURL(item.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const removeVideo = (id) => {
    setVideos((prev) => {
      const item = prev.find((v) => v.id === id);
      if (item) URL.revokeObjectURL(item.preview);
      return prev.filter((v) => v.id !== id);
    });
  };

  const handleSubmit = async () => {
    setUploading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      
      onSubmit({
        pictures: pictures.map((p) => p.file),
        videos: videos.map((v) => v.file),
        description,
      });
      
      // Cleanup
      pictures.forEach((p) => URL.revokeObjectURL(p.preview));
      videos.forEach((v) => URL.revokeObjectURL(v.preview));
      setPictures([]);
      setVideos([]);
      setDescription("");
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">
            Complete Job
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <p className="text-xs text-gray-600 dark:text-gray-300">
            Upload photos/videos if you want. All fields are optional.
          </p>

          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Description <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Add any notes about the completed work..."
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#EFA938]"
            />
          </div>

          {/* Pictures Section */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Pictures <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {pictures.map((picture) => (
                <div key={picture.id} className="relative group">
                  <img
                    src={picture.preview}
                    alt="Uploaded"
                    className="w-full h-16 object-cover rounded-lg border-2 border-gray-200 dark:border-slate-600"
                  />
                  <button
                    onClick={() => removePicture(picture.id)}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {pictures.length < 10 && (
                <label className="flex flex-col items-center justify-center h-16 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-[#EFA938] dark:hover:border-[#EFA938] transition-colors">
                  <ImageIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mb-1" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">Add Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePictureUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Videos Section */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Videos <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-3 mb-3">
              {videos.map((video) => (
                <div key={video.id} className="relative group">
                  <video
                    src={video.preview}
                    controls
                    className="w-full h-16 object-cover rounded-lg border-2 border-gray-200 dark:border-slate-600"
                  />
                  <button
                    onClick={() => removeVideo(video.id)}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {videos.length < 5 && (
                <label className="flex flex-col items-center justify-center h-16 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-[#EFA938] dark:hover:border-[#EFA938] transition-colors">
                  <Video className="h-5 w-5 text-gray-400 dark:text-gray-500 mb-1" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">Add Video</span>
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 px-4 py-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-sm text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploading}
            className="px-4 py-2 rounded-lg bg-[#EFA938] text-white text-sm font-semibold hover:bg-[#d78c1f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {uploading ? (
              <>
                <Upload className="h-4 w-4 animate-pulse" />
                Submitting...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Submit
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompleteJobDialog;

